/**
 * EnvelopeNode - creates ADSR envelope data
 *
 * Endpoints:
 * - attack (input/output): attack time in seconds
 * - decay (input/output): decay time in seconds
 * - sustain (input/output): sustain level (0-1)
 * - release (input/output): release time in seconds
 * - envelope (output): compound envelope data { attack, decay, sustain, release }
 *
 * Connect envelope output to synth's envelope input for compound control.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { EnvelopeData } from '@nodes/EnvelopeData';
import { params } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const endpoints = params({
  envelope: { dataType: 'envelope', isOutput: true, primary: true },
  attack: { value: 0.01, min: 0.001, max: 2, step: 0.01 },
  decay: { value: 0.1, min: 0.001, max: 2, step: 0.01 },
  sustain: { value: 0.5, min: 0, max: 1, step: 0.01 },
  release: { value: 0.2, min: 0.001, max: 2, step: 0.01 }
})


// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

export class EnvelopeNode extends BaseNode<typeof endpoints> {
  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    // Emit initial envelope after store values are set
    this.emitEnvelope();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    // Any ADSR value change triggers envelope output
    if (['attack', 'decay', 'sustain', 'release'].includes(endpointId)) {
      this.emitEnvelope();
    }
  }

  private emitEnvelope(): void {
    this.params.envelope.set({
      attack: this.params.attack.value,
      decay: this.params.decay.value,
      sustain: this.params.sustain.value,
      release: this.params.release.value
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const envelopeNodeDef: NodeDef<EnvelopeNode> = {
  type: 'Envelope',
  shortName: 'env',
  info: 'ADSR envelope generator. Connect to a synth to shape note dynamics.',
  domain: Domain.Audio,
  endpoints,
  ctor: EnvelopeNode
};
